import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { apiClient } from '../services/api'

interface AnalyticsDetailModalProps {
  type: 'contacts' | 'templates' | 'sends' | 'likes' | 'views'
  onClose: () => void
}

export default function AnalyticsDetailModal({ type, onClose }: AnalyticsDetailModalProps) {
  const [page, setPage] = useState(0)
  const [expandedSessions, setExpandedSessions] = useState<Set<number>>(new Set())
  const limit = 50

  const { data: contacts, isLoading: contactsLoading } = useQuery({
    queryKey: ['analytics-contacts', page],
    queryFn: () => apiClient.getAnalyticsContacts(page * limit, limit),
    enabled: type === 'contacts'
  })

  const { data: templates, isLoading: templatesLoading } = useQuery({
    queryKey: ['analytics-templates', page],
    queryFn: () => apiClient.getAnalyticsTemplates(page * limit, limit),
    enabled: type === 'templates'
  })

  const { data: sends, isLoading: sendsLoading } = useQuery({
    queryKey: ['analytics-sends', page],
    queryFn: () => apiClient.getAnalyticsSends(page * limit, limit, 'sent'),
    enabled: type === 'sends'
  })

  const { data: likes, isLoading: likesLoading } = useQuery({
    queryKey: ['analytics-likes', page],
    queryFn: () => apiClient.getRecentLikes(limit, page * limit),
    enabled: type === 'likes'
  })

  const { data: views, isLoading: viewsLoading } = useQuery({
    queryKey: ['analytics-views', page],
    queryFn: () => apiClient.getRecentViews(limit, page * limit),
    enabled: type === 'views'
  })

  const isLoading = contactsLoading || templatesLoading || sendsLoading || likesLoading || viewsLoading

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Function to group logs by day + IP + User Agent + email
  const groupBySession = (logs: any[]) => {
    if (!logs || logs.length === 0) return []
    
    // Group by session key (date + IP + userAgent + email)
    const groupMap = new Map<string, any[]>()
    
    logs.forEach(log => {
      const date = new Date(log.created_at).toDateString()
      const sessionKey = `${date}_${log.ip_address || 'no-ip'}_${log.user_agent || 'no-agent'}_${log.email || 'no-email'}`
      
      if (!groupMap.has(sessionKey)) {
        groupMap.set(sessionKey, [])
      }
      groupMap.get(sessionKey)!.push(log)
    })
    
    // Convert to array with metadata
    const groups: any[] = []
    let groupId = 0
    
    groupMap.forEach((items, key) => {
      // Sort by created_at descending
      items.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      
      // Find first item with location
      const firstItemWithLocation = items.find(item => item.location && item.location !== '-' && item.location !== null)
      
      groups.push({
        id: groupId++,
        key: key,
        items: items,
        firstItem: items[0],
        count: items.length,
        date: new Date(items[0].created_at).toDateString(),
        location: firstItemWithLocation?.location
      })
    })
    
    // Sort groups by date descending
    groups.sort((a, b) => new Date(b.firstItem.created_at).getTime() - new Date(a.firstItem.created_at).getTime())
    
    return groups
  }

  const groupedLikes = groupBySession(likes || [])
  const groupedViews = groupBySession(views || [])
  
  const toggleSession = (sessionId: number) => {
    const newExpanded = new Set(expandedSessions)
    if (newExpanded.has(sessionId)) {
      newExpanded.delete(sessionId)
    } else {
      newExpanded.add(sessionId)
    }
    setExpandedSessions(newExpanded)
  }

  const getTitle = () => {
    switch (type) {
      case 'contacts':
        return 'Unique Contacts'
      case 'templates':
        return 'Template Statistics'
      case 'sends':
        return 'Successful Sends'
      case 'likes':
        return 'Recent Likes'
      case 'views':
        return 'Recent Views'
      default:
        return ''
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold">{getTitle()}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <>
              {/* Contacts Table */}
              {type === 'contacts' && contacts && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Templates Sent
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Last Activity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {contacts.map((contact: any) => (
                        <tr key={contact.email} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {contact.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {contact.templates_sent}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {contact.last_sent_at ? formatDate(contact.last_sent_at) : 'Never'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(contact.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {contacts.length === 0 && (
                    <div className="text-center py-8 text-gray-500">No contacts found</div>
                  )}
                </div>
              )}

              {/* Templates Table */}
              {type === 'templates' && templates && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Template
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Sends
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Views
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Popularity
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {templates.map((template: any) => (
                        <tr key={template.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm">
                            <div className="flex items-center space-x-3">
                              {template.preview_image && (
                                <img
                                  src={template.preview_image}
                                  alt={template.name}
                                  className="w-12 h-12 object-cover rounded"
                                />
                              )}
                              <span className="font-medium text-gray-900">{template.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {template.category || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full font-medium">
                              {template.send_count}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-medium">
                              {template.view_count}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full font-medium">
                              {template.popularity_score}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {templates.length === 0 && (
                    <div className="text-center py-8 text-gray-500">No templates found</div>
                  )}
                </div>
              )}

              {/* Sends Table */}
              {type === 'sends' && sends && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Template
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Sent At
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sends.map((send: any) => (
                        <tr key={send.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {send.email}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {send.template_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {send.template_category || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {send.sent_at ? formatDate(send.sent_at) : formatDate(send.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                              {send.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {sends.length === 0 && (
                    <div className="text-center py-8 text-gray-500">No sends found</div>
                  )}
                </div>
              )}

              {type === 'likes' && likes && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Template
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ESID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          IP / Location
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User Agent
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Liked At
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {groupedLikes.map((session: any) => (
                        <>
                          {session.count === 1 ? (
                            <tr key={session.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                {session.firstItem.template_name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                {session.firstItem.esputnik_message_id}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <div>
                                  <div>{session.firstItem.ip_address || '-'}</div>
                                  {session.location && session.location !== '-' && (
                                    <div className="text-xs text-gray-400">{session.location}</div>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                                {session.firstItem.user_agent || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {session.firstItem.email || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(session.firstItem.created_at)}
                              </td>
                            </tr>
                          ) : !expandedSessions.has(session.id) ? (
                            <tr 
                              key={session.id} 
                              className="hover:bg-gray-50 cursor-pointer"
                              onClick={() => toggleSession(session.id)}
                            >
                              <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                  <span>{session.count} likes</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                {session.firstItem.esputnik_message_id}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <div>
                                  <div>{session.firstItem.ip_address || '-'}</div>
                                  {session.location && session.location !== '-' && (
                                    <div className="text-xs text-gray-400">{session.location}</div>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                                {session.firstItem.user_agent || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {session.firstItem.email || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(session.firstItem.created_at)}
                              </td>
                            </tr>
                          ) : (
                            <>
                              <tr 
                                key={`summary-${session.id}`}
                                className="hover:bg-gray-50 cursor-pointer bg-blue-50"
                                onClick={() => toggleSession(session.id)}
                              >
                                <td className="px-6 py-4 text-sm font-medium text-blue-900">
                                  <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                    <span>{session.count} likes</span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-900 font-mono">
                                  {session.firstItem.esputnik_message_id}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-900">
                                  <div>
                                    <div>{session.firstItem.ip_address || '-'}</div>
                                    {session.location && session.location !== '-' && (
                                      <div className="text-xs text-blue-400">{session.location}</div>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-sm text-blue-900 max-w-xs truncate">
                                  {session.firstItem.user_agent || '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-900">
                                  {session.firstItem.email || '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-900">
                                  {formatDate(session.firstItem.created_at)}
                                </td>
                              </tr>
                              {session.items.map((like: any) => {
                                const location = like.location || session.location;
                                return (
                                <tr key={like.id} className="hover:bg-gray-50 bg-gray-50">
                                  <td className="px-6 py-4 text-sm font-medium text-gray-900 pl-12">
                                    {like.template_name}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                    {like.esputnik_message_id}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <div>
                                      <div>{like.ip_address || '-'}</div>
                                      {location && location !== '-' && (
                                        <div className="text-xs text-gray-400">{location}</div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                                    {like.user_agent || '-'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {like.email || '-'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {formatDate(like.created_at)}
                                  </td>
                                </tr>
                              );
                              })}
                            </>
                          )}
                        </>
                      ))}
                    </tbody>
                  </table>
                  {likes.length === 0 && (
                    <div className="text-center py-8 text-gray-500">No likes found</div>
                  )}
                </div>
              )}

              {type === 'views' && views && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Template
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ESID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          IP / Location
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User Agent
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Viewed At
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {groupedViews.map((session: any) => (
                        <>
                          {session.count === 1 ? (
                            <tr key={session.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                {session.firstItem.template_name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                {session.firstItem.esputnik_message_id}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <div>
                                  <div>{session.firstItem.ip_address || '-'}</div>
                                  {session.location && session.location !== '-' && (
                                    <div className="text-xs text-gray-400">{session.location}</div>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                                {session.firstItem.user_agent || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {session.firstItem.email || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(session.firstItem.created_at)}
                              </td>
                            </tr>
                          ) : !expandedSessions.has(session.id) ? (
                            <tr 
                              key={session.id} 
                              className="hover:bg-gray-50 cursor-pointer"
                              onClick={() => toggleSession(session.id)}
                            >
                              <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                  <span>{session.count} views</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                {session.firstItem.esputnik_message_id}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <div>
                                  <div>{session.firstItem.ip_address || '-'}</div>
                                  {session.location && session.location !== '-' && (
                                    <div className="text-xs text-gray-400">{session.location}</div>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                                {session.firstItem.user_agent || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {session.firstItem.email || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(session.firstItem.created_at)}
                              </td>
                            </tr>
                          ) : (
                            <>
                              <tr 
                                key={`summary-${session.id}`}
                                className="hover:bg-gray-50 cursor-pointer bg-blue-50"
                                onClick={() => toggleSession(session.id)}
                              >
                                <td className="px-6 py-4 text-sm font-medium text-blue-900">
                                  <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                    <span>{session.count} views</span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-900 font-mono">
                                  {session.firstItem.esputnik_message_id}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-900">
                                  <div>
                                    <div>{session.firstItem.ip_address || '-'}</div>
                                    {session.location && session.location !== '-' && (
                                      <div className="text-xs text-blue-400">{session.location}</div>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-sm text-blue-900 max-w-xs truncate">
                                  {session.firstItem.user_agent || '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-900">
                                  {session.firstItem.email || '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-900">
                                  {formatDate(session.firstItem.created_at)}
                                </td>
                              </tr>
                              {session.items.map((view: any) => {
                                const location = view.location || session.location;
                                return (
                                <tr key={view.id} className="hover:bg-gray-50 bg-gray-50">
                                  <td className="px-6 py-4 text-sm font-medium text-gray-900 pl-12">
                                    {view.template_name}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                    {view.esputnik_message_id}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <div>
                                      <div>{view.ip_address || '-'}</div>
                                      {location && location !== '-' && (
                                        <div className="text-xs text-gray-400">{location}</div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                                    {view.user_agent || '-'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {view.email || '-'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {formatDate(view.created_at)}
                                  </td>
                                </tr>
                              );
                              })}
                            </>
                          )}
                        </>
                      ))}
                    </tbody>
                  </table>
                  {views.length === 0 && (
                    <div className="text-center py-8 text-gray-500">No views found</div>
                  )}
                </div>
              )}

              {/* Pagination */}
              <div className="mt-6 flex items-center justify-between">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {page + 1}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={
                    (type === 'contacts' && contacts && contacts.length < limit) ||
                    (type === 'templates' && templates && templates.length < limit) ||
                    (type === 'sends' && sends && sends.length < limit)
                  }
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

